<script setup>
/**
 * "Try wording you have not saved" — the draft override. THE PLAN, §4 P6.
 *
 * WHY THIS IS THE MOST IMPORTANT CONTROL ON THE SCREEN, and it is not obvious:
 * without it, the only way to find out how a change to the Personality page behaves is to
 * SAVE IT FIRST — which publishes it to every live WhatsApp customer in the same instant.
 * A test screen that can only test what is already live is a test screen you use after the
 * fact. Worse, the state right after applying a first-run template is deliberately
 * unsaved, so an owner who tried the assistant at that moment was silently rehearsing the
 * shipped default and concluding the template had done nothing.
 *
 * THE RULES IT KEEPS, all three learned from the version this replaces:
 *  • IT SAVES NOTHING, and says so twice — in the note, and again in the banner that
 *    appears the moment the text differs from what is stored.
 *  • AN EMPTY BOX IS AN ERROR, NEVER A SILENT FALL-BACK. Rehearsing wording other than the
 *    wording on screen is the exact bug this control exists to kill, so the BFF answers
 *    422 on it and this component refuses to send it in the first place.
 *  • IT SHOWS WHAT IS SAVED WHEN IT ARRIVES, and stays usable when it does not. A failed
 *    read of the saved wording disables the box and says why; it never renders an empty
 *    box that looks like "your assistant has no instructions".
 */
import { computed } from 'vue';

import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import FirstDraftLink from './FirstDraftLink.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** What is on screen. Empty string is a real value the owner can type themselves. */
  modelValue: { type: String, default: '' },
  /** What is stored today, or null while it is unknown / unreadable. */
  saved: { type: String, default: null },
  /** The stored wording is still the one we shipped. */
  isDefault: { type: Boolean, default: false },
  /** The saved wording could not be read. */
  isUnavailable: { type: Boolean, default: false },
  message: { type: String, default: '' },
  /**
   * Forced open by the page when a rejection names this box.
   *
   * A validation message rendered inside a COLLAPSED panel is a message nobody reads, and
   * the owner is left with a refused send and nothing on screen explaining it. `Accordion`
   * watches this prop, so raising it opens the panel; it is never lowered again, because
   * yanking a panel shut under someone who has just been told to fix something in it is
   * the opposite of the fix.
   */
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const isChanged = computed(
  () => props.saved !== null && props.modelValue !== props.saved
);
</script>

<template>
  <Accordion
    :title="t('WASLO.TEST.SETUP.WORDING')"
    :is-open="isOpen"
    data-waslo-test-wording
  >
    <div class="flex flex-col gap-4">
      <p class="mb-0 text-sm leading-6 max-w-[58ch] text-n-slate-11">
        {{ t('WASLO.TEST.WORDING.NOTE') }}
      </p>

      <Banner v-if="isUnavailable" color="slate">
        {{ t('WASLO.TEST.WORDING.UNAVAILABLE') }}
      </Banner>
      <Banner v-else-if="isDefault" color="slate">
        {{ t('WASLO.TEST.WORDING.DEFAULT') }}
      </Banner>
      <!-- P-8.1: the wording being tried is Waslo's own, so offer the owner's. -->
      <FirstDraftLink v-if="isDefault && !isUnavailable" />
      <!-- `n-blue-*` is the accent — the overlay remaps it emerald in BOTH themes, which
           most `n-brand` utilities silently fail to do (THE PLAN, §5 R3). -->
      <Banner v-else-if="isChanged" color="blue" data-waslo-wording-changed>
        {{ t('WASLO.TEST.WORDING.CHANGED') }}
      </Banner>

      <WasloTextArea
        :model-value="modelValue"
        :label="t('WASLO.TEST.WORDING.LABEL')"
        :hint="t('WASLO.TEST.WORDING.HINT')"
        :disabled="isUnavailable"
        :message="message"
        message-type="error"
        :max-length="20000"
        min-height="8rem"
        @update:model-value="emit('update:modelValue', $event)"
      />

      <div v-if="isChanged" class="flex">
        <Button
          :label="t('WASLO.TEST.WORDING.RESET')"
          variant="outline"
          color="slate"
          size="sm"
          data-waslo-wording-reset
          @click="emit('update:modelValue', saved)"
        />
      </div>
    </div>
  </Accordion>
</template>
